import CT from '@marketplace-ai/tags';
import type { ColDef, GetRowIdParams } from 'ag-grid-community';
import {
  AllCommunityModule,
  colorSchemeLightCold,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef } from 'react';
import { EmptyState, LoadingSpinner } from '~/components';
import { FilterBanner, useFilterBanner } from '~/components/agGrid/FilterBanner';
import {
  ACTIONS_COLUMN_BASE,
  ActionsRenderer,
  BASE_GRID_STYLE,
  type ActionDef,
} from '~/components/Table/gridUtil';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { withNewRecordPill } from '../../newRecords/NewRecordPill';
import { useNewRecordPills } from '../../newRecords/useNewRecords';

import { deaColumns } from './constants';
import { DeaLicenseFormValues } from './types';

ModuleRegistry.registerModules([AllCommunityModule]);
const theme = themeQuartz.withPart(colorSchemeLightCold).withParams(BASE_GRID_STYLE);

const DeaNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.licenses}
    title="No DEA licenses yet"
    cyTag={CT.account.licenses.dea.table.emptyState}
  />
);

const getRowId = (params: GetRowIdParams<DeaLicenseFormValues>) => String(params.data.id);

interface Props {
  deaLicenses: DeaLicenseFormValues[];
  loading: boolean;
  handleDeleteClick: (id: number) => void;
  handleEditClick: (id: number) => void;
}
export const DeaGrid = (props: Props) => {
  const { deaLicenses, loading, handleDeleteClick, handleEditClick } = props;

  const colDefs = useMemo<ColDef[]>(
    () => [
      { field: 'id', hide: true },
      ...deaColumns(),
      {
        ...ACTIONS_COLUMN_BASE,
        width: 80,
        cellRenderer: ActionsRenderer,
        cellRendererParams: {
          actions: [
            { type: 'edit', handler: handleEditClick, cyTag: CT.certs.table.editBtn },
            { type: 'delete', handler: handleDeleteClick, cyTag: CT.certs.table.deleteBtn },
          ] satisfies ActionDef[],
        },
      },
    ],
    [handleEditClick, handleDeleteClick]
  );

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      cellStyle: { display: 'flex', alignItems: 'center' },
      filter: true,
      resizable: true,
      flex: 1,
      wrapText: true,
    };
  }, []);

  const gridRef = useRef<AgGridReact>(null);
  // [MCV-1812] Records a document parse just created carry a "New" pill for this visit.
  const newRecordIds = useNewRecordPills('dea_licenses', deaLicenses);
  const columnDefs = useMemo(
    () => withNewRecordPill(colDefs, newRecordIds),
    [colDefs, newRecordIds]
  );
  const { filtersActive, handleFilterChanged, clearFilters, registerGridApi } = useFilterBanner();

  return (
    <div className="h-full w-full min-h-100 p-0 overflow-auto rounded-lg border border-grey-900 flex flex-col">
      <FilterBanner active={filtersActive} onClear={clearFilters} />
      <div className="flex-1 min-h-0">
        <AgGridReact
          theme={theme}
          colResizeDefault="shift"
          enableFilterHandlers={true}
          getRowId={getRowId}
          rowData={deaLicenses}
          loading={loading}
          loadingOverlayComponent={() => <LoadingSpinner size={12} />}
          noRowsOverlayComponent={DeaNoRowsOverlay}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationAutoPageSize={true}
          onFilterChanged={handleFilterChanged}
          onGridReady={(params) => registerGridApi(params.api)}
          ref={gridRef}
        />
      </div>
    </div>
  );
};
